import { Component, DestroyRef, effect, inject, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
    MatTableDataSource,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
} from '@angular/material/table';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { Service } from 'src/app/models/centrex/service';
import { DialogDeleteConfirmationComponent } from '../../dialogs/dialog-delete-confirmation/dialog-delete-confirmation.component';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { ServiceService } from 'src/app/services/centrex/service.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { filter, switchMap } from 'rxjs';
import { MatTooltip } from '@angular/material/tooltip';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { MatButton, MatIconButton } from '@angular/material/button';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-services',
    templateUrl: './services.component.html',
    styleUrls: ['./services.component.scss'],
    imports: [
        FaIconComponent,
        MatButton,
        RouterLink,
        MatIcon,
        ReactiveFormsModule,
        MatTable,
        MatSort,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatSortHeader,
        MatFormField,
        MatInput,
        MatIconButton,
        MatSuffix,
        MatTooltip,
        MatCellDef,
        MatCell,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatPaginator,
        TranslateModule,
    ]
})
export class ServicesComponent implements OnInit {
    private readonly instanceService = inject(InstanceService);
    private destroyRef = inject(DestroyRef);

    // Icône
    public faAngleRight = faAngleRight;

    displayedColumns: string[] = ['name', 'actions'];
    dataSource: MatTableDataSource<Service>;
    deleteConfirmationDialog: MatDialogRef<DialogDeleteConfirmationComponent>;

    filtersForm: UntypedFormGroup;

    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: false }) sort: MatSort;

    constructor(
        private serviceService: ServiceService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private toastr: ToastrService,
        private formbuilder: UntypedFormBuilder,
        private spinner: NgxSpinnerService
    ) {
        this.filtersForm = new UntypedFormGroup({});

        this.initFiltersForm();

        effect(() => {
            if (this.instanceService.instance()) {
                this.loadDatas();
                this.initFiltersForm();
            }
        });
    }

    ngOnInit(): void {
        this.initFiltersForm();

        this.loadDatas();
    }

    /**
     * Initialisation du formulaire des filtres du tableau des services
     */
    initFiltersForm() {
        this.filtersForm = this.formbuilder.group({
            name: [''],
        });
    }

    loadDatas() {
        this.spinner.show();
        this.serviceService
            .getServices()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (services) => {
                    this.dataSource = new MatTableDataSource(services);

                    this.dataSource.paginator = this.paginator;
                    this.dataSource.sort = this.sort;

                    this.createFilters();

                    this.spinner.hide();
                },
                error: () => {
                    this.spinner.hide();
                },
            });
    }

    createFilters() {
        this.dataSource.filterPredicate = (data: Service, filter: string) => {
            const filterArray = filter.split('¤');
            const matchFilter = [];

            matchFilter.push(data.name && data.name.trim().toLowerCase().includes(filterArray[0].trim().toLowerCase()));

            return matchFilter.every(Boolean);
        };

        this.displayedColumns = ['name', 'actions'];

        this.applyFilters();
    }

    /**
     * Application du filtre dans le tableau des services
     */
    applyFilters() {
        const name = this.filtersForm.get('name').value;

        const filterValue = !!name ? name : '';

        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    /**
     * Réinitialise le filtre de la colonne spécifiée.
     * @param formControlName Nom du contrôle du formulaire
     */
    resetColumnFilter(formControlName: string): void {
        this.filtersForm.controls[formControlName].reset();
        this.applyFilters();
    }

    /**
     * Réinitialise tous les filtres de colonne
     */
    resetAllColumnFilter() {
        this.filtersForm.reset();
        this.applyFilters();
    }

    onDeleteClick(serviceName: string) {
        this.deleteConfirmationDialog = this.dialog.open(DialogDeleteConfirmationComponent, {
            panelClass: 'default-dialog',
            disableClose: true,
        });
        this.deleteConfirmationDialog.componentInstance.confirmMessage = this.translate.instant('DialogDeleteConfirmation.ServiceConfirmMessage', { serviceName: serviceName });

        this.deleteConfirmationDialog
            .afterClosed()
            .pipe(
                filter((result) => result === true),
                switchMap(() => this.serviceService.deleteService(serviceName)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.toastr.success(this.translate.instant('Services.SuccessMessageDeleteService', { serviceName: serviceName }));
                    this.loadDatas();
                },
                error: () => {
                    this.toastr.error(this.translate.instant('Services.ErrorMessageDeleteService', { serviceName: serviceName }));
                },
                complete: () => {
                    this.deleteConfirmationDialog = null;
                },
            });
    }
}
